/**
 * Finance Module Zone Detection
 *
 * Zones group related pages to reduce sidebar complexity.
 * Each zone shows only its relevant navigation items.
 */

import { getTenantTax } from '@/lib/tenant-format'

export type FinanceZone = 'start' | 'reports' | 'entry' | 'queues' | 'setup'

export interface ZoneConfig {
  zone: FinanceZone
  label: string
  description: string
  basePath: string
}

export const ZONE_CONFIGS: Record<FinanceZone, ZoneConfig> = {
  start: {
    zone: 'start',
    label: 'Finance',
    description: 'Quick Find',
    basePath: '/finance',
  },
  reports: {
    zone: 'reports',
    label: 'Reports',
    description: 'Financial statements and analysis',
    basePath: '/finance/reports',
  },
  entry: {
    zone: 'entry',
    label: 'Transactions',
    description: 'Enter and manage vouchers',
    basePath: '/finance/transactions',
  },
  queues: {
    zone: 'queues',
    label: 'Work Queues',
    description: 'Collections, payments, cheques',
    basePath: '/finance/receivables',
  },
  setup: {
    zone: 'setup',
    label: 'Setup',
    description: 'Accounts and configuration',
    basePath: '/finance/settings',
  },
}

/**
 * Navigation items for each zone
 */
export interface ZoneNavItem {
  label: string
  href: string
  icon?: string
  /** Optional group header for organizing items */
  group?: string
  /**
   * RBAC v1 — permission required to show this nav item. Per spec #10 §3.3.11.
   * Null = always visible (no gate).
   */
  requiredPermission?: string | null
}

export const ZONE_NAV_ITEMS: Record<Exclude<FinanceZone, 'start'>, ZoneNavItem[]> = {
  reports: [
    { label: 'All Reports', href: '/finance/reports', requiredPermission: null }, // hub — let any report perm cover it
    { label: 'Balance Sheet', href: '/finance/reports/balance-sheet', group: 'Statements', requiredPermission: 'report:balance-sheet' },
    { label: 'Profit & Loss', href: '/finance/reports/profit-loss', group: 'Statements', requiredPermission: 'report:profit-loss' },
    { label: 'Trial Balance', href: '/finance/reports/trial-balance', group: 'Statements', requiredPermission: 'report:trial-balance' },
    { label: 'General Ledger', href: '/finance/reports/general-ledger', group: 'Analysis', requiredPermission: 'report:general-ledger' },
    { label: 'Aging Reports', href: '/finance/reports/aging/receivables', group: 'Analysis', requiredPermission: 'report:aging-receivables' },
    { label: 'Cash Position', href: '/finance/reports/cash-position', group: 'Analysis', requiredPermission: 'report:cash-position' },
    // DISPLAY label resolves from the tenant tax label at access time (getter);
    // route path + permission key are STABLE — do not rename them.
    { get label() { return `${getTenantTax().taxLabel} Report` }, href: '/finance/reports/vat', group: 'Compliance', requiredPermission: 'report:vat' },
  ],
  entry: [
    { label: 'Daily Overview', href: '/finance/dashboard', requiredPermission: 'finance:access' },
    { label: 'Transactions', href: '/finance/transactions', requiredPermission: 'voucher:view' },
    { label: 'Invoices', href: '/finance/invoices', requiredPermission: 'invoice:view' },
    { label: 'Allocations', href: '/finance/allocations', requiredPermission: 'allocation:view' },
  ],
  queues: [
    { label: 'Collections', href: '/finance/receivables', requiredPermission: 'report:aging-receivables' },
    // Calendar — the finance mount of the one SchedulePage (phase 10). Gated on
    // calendar-event:view, so it does not appear for a role that cannot read the calendar.
    //
    // **ONE entry.** To-do used to sit beside this as its own row pointing at `?view=todo`; it is
    // a TAB on this page, and two rows taught people they were two places. `/finance/follow-ups`
    // still redirects into the queue view, so old bookmarks land where they always did.
    { label: 'Calendar', href: '/finance/schedule', requiredPermission: 'calendar-event:view' },
    // Internal task management (Action Items module) — the heavier sibling of a
    // planned activity. Finance mount keeps the Work Queues sidebar active.
    { label: 'Action Items', href: '/finance/action-items', requiredPermission: 'action-item:view' },
    { label: 'Payments Due', href: '/finance/payables', requiredPermission: 'report:aging-payables' },
    { label: 'Cheque Tracking', href: '/finance/cheques', requiredPermission: 'cheque:view' },
  ],
  setup: [
    { label: 'Chart of Accounts', href: '/finance/masters/accounts', group: 'Masters', requiredPermission: 'ledger:view' },
    { label: 'Subledgers', href: '/finance/masters/parties', group: 'Masters', requiredPermission: 'subledger:view' },
    { label: 'Voucher Types', href: '/finance/settings/voucher-types', group: 'Settings', requiredPermission: 'voucher-type:view' },
    { label: 'Account Mapping', href: '/finance/settings/account-mapping', group: 'Settings', requiredPermission: 'account-mapping:view' },
    { label: 'Currencies & Exchange Rates', href: '/finance/settings/currencies', group: 'Settings', requiredPermission: 'currency:view' },
    { label: 'Fiscal Periods', href: '/finance/settings/periods', group: 'Settings', requiredPermission: 'fiscal-period:view' },
    { label: 'Audit Trail', href: '/finance/settings/audit-log', group: 'Settings', requiredPermission: 'audit-trail:view' },
  ],
}

/**
 * Helper — filters [items] by what the user can see based on their permission set.
 *
 * Use with `useUserPermissions()`:
 * ```
 * const perms = useUserPermissions()
 * const visible = filterZoneItemsByPermissions(ZONE_NAV_ITEMS[zone], perms)
 * ```
 */
export function filterZoneItemsByPermissions(
  items: ZoneNavItem[],
  userPermissions: string[]
): ZoneNavItem[] {
  return items.filter((item) => {
    if (item.requiredPermission == null) return true
    return userPermissions.includes(item.requiredPermission)
  })
}

/**
 * Detect which zone a path belongs to
 */
export function detectZone(pathname: string): FinanceZone {
  // Exact match for start page
  if (pathname === '/finance' || pathname === '/finance/') {
    return 'start'
  }

  // Reports zone
  if (pathname.startsWith('/finance/reports')) {
    return 'reports'
  }

  // Entry zone (transactions, invoices, allocations)
  if (
    pathname.startsWith('/finance/transactions') ||
    pathname.startsWith('/finance/invoices') ||
    pathname.startsWith('/finance/allocations')
  ) {
    return 'entry'
  }

  // Queues zone (receivables, payables, cheques, follow-ups, action items)
  if (
    pathname.startsWith('/finance/receivables') ||
    pathname.startsWith('/finance/payables') ||
    pathname.startsWith('/finance/cheques') ||
    pathname.startsWith('/finance/follow-ups') ||
    pathname.startsWith('/finance/schedule') ||
    pathname.startsWith('/finance/action-items')
  ) {
    return 'queues'
  }

  // Setup zone (masters, settings)
  if (
    pathname.startsWith('/finance/masters') ||
    pathname.startsWith('/finance/settings')
  ) {
    return 'setup'
  }

  // Dashboard goes to entry zone (it's operational)
  if (pathname === '/finance/dashboard') {
    return 'entry'
  }

  // Guide page - no sidebar needed, treat as start
  if (pathname.startsWith('/finance/guide')) {
    return 'start'
  }

  // Default to start
  return 'start'
}

/**
 * Build breadcrumb segments for a finance path
 */
export interface BreadcrumbSegment {
  label: string
  href: string
}

export function buildBreadcrumbs(pathname: string): BreadcrumbSegment[] {
  const segments: BreadcrumbSegment[] = [
    { label: 'Finance Menu', href: '/finance' },
  ]

  const zone = detectZone(pathname)

  if (zone === 'start') {
    return segments
  }

  // Add zone segment
  const zoneConfig = ZONE_CONFIGS[zone]
  segments.push({
    label: zoneConfig.label,
    href: zoneConfig.basePath,
  })

  // Add page-specific segment if not on zone root
  const pageLabels: Record<string, string> = {
    '/finance/reports/balance-sheet': 'Balance Sheet',
    '/finance/reports/profit-loss': 'Profit & Loss',
    '/finance/reports/trial-balance': 'Trial Balance',
    '/finance/reports/general-ledger': 'General Ledger',
    '/finance/reports/cash-position': 'Cash Position',
    '/finance/reports/vat': `${getTenantTax().taxLabel} Report`,
    '/finance/reports/aging/receivables': 'Receivables Aging',
    '/finance/reports/aging/payables': 'Payables Aging',
    '/finance/transactions': 'All Transactions',
    '/finance/invoices': 'Invoices',
    '/finance/allocations': 'Allocations',
    '/finance/receivables': 'Collections',
    '/finance/follow-ups': 'To-do',   // the redirect target still needs a label mid-navigation
    '/finance/schedule': 'Calendar',
    '/finance/action-items': 'Action Items',
    '/finance/payables': 'Payments Due',
    '/finance/cheques': 'Cheque Tracking',
    '/finance/masters/accounts': 'Chart of Accounts',
    '/finance/masters/parties': 'Subledgers',
    '/finance/settings': 'Settings',
    '/finance/settings/voucher-types': 'Voucher Types',
    '/finance/settings/account-mapping': 'Account Mapping',
    '/finance/settings/currencies': 'Currencies & Exchange Rates',
    '/finance/settings/periods': 'Fiscal Periods',
    '/finance/settings/audit-log': 'Audit Trail',
    '/finance/dashboard': 'Daily Overview',
  }

  // Check for exact match
  if (pageLabels[pathname] && pathname !== zoneConfig.basePath) {
    segments.push({
      label: pageLabels[pathname],
      href: pathname,
    })
  }

  // Check for statement pages
  if (pathname.startsWith('/finance/reports/statement/customer')) {
    segments.push({ label: 'Customer Statement', href: pathname })
  } else if (pathname.startsWith('/finance/reports/statement/vendor')) {
    segments.push({ label: 'Vendor Statement', href: pathname })
  }

  return segments
}

/**
 * Check if sidebar should be hidden for a path
 */
export function shouldHideSidebar(pathname: string): boolean {
  const zone = detectZone(pathname)
  return zone === 'start'
}
